/**
 * CreateMcpKeyModal - Modal for creating new MCP API keys
 *
 * Features:
 * - Name and description input
 * - Curated permission group selection (simplified UX)
 * - Optional expiration date
 * - Shows generated key after creation (ONLY ONCE!)
 * - Green ring animation on copy
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
  Spinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui';
import { Bot, Copy, Check, AlertTriangle, Eye, Play, ShieldCheck, CheckCircle } from 'lucide-react';
import { createMcpKey, McpKeyCreateResponse, McpPermissionGroup } from '@/services/mcpKeyService';
import { MCP_PERMISSION_GROUPS } from '@simcheck/shared-types';

interface CreateMcpKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onSuccess?: () => void;
}

type Step = 'form' | 'success';

const permissionIcons: Record<McpPermissionGroup, React.ReactNode> = {
  read_only: <Eye className="w-5 h-5" />,
  execute_flows: <Play className="w-5 h-5" />,
  full_access: <ShieldCheck className="w-5 h-5" />,
};

export function CreateMcpKeyModal({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
}: CreateMcpKeyModalProps) {
  // Step management
  const [step, setStep] = useState<Step>('form');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissionGroup, setPermissionGroup] = useState<McpPermissionGroup>('read_only');
  const [expiresIn, setExpiresIn] = useState<string>('never');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate expiration date
      let expiresAt: Date | undefined;
      if (expiresIn !== 'never') {
        const now = new Date();
        switch (expiresIn) {
          case '30d':
            expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
            break;
          case '90d':
            expiresAt = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
            break;
          case '1y':
            expiresAt = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
            break;
        }
      }

      const response = await createMcpKey(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          permissionGroup,
          expiresAt,
        },
        organizationId
      );

      setGeneratedKey(response.key);
      setStep('success');
      onSuccess?.();
    } catch (err: any) {
      console.error('Error creating MCP key:', err);
      setError(err.message || 'Failed to create MCP key');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [generatedKey]);

  const handleClose = () => {
    // Reset state
    setStep('form');
    setName('');
    setDescription('');
    setPermissionGroup('read_only');
    setExpiresIn('never');
    setError('');
    setGeneratedKey('');
    setCopied(false);
    setConfirmed(false);
    onClose();
  };

  // Render form step
  const renderForm = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          Create MCP Key
        </DialogTitle>
        <DialogDescription>
          Create a new API key for AI assistants like Claude Desktop and Cursor
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            placeholder="e.g., Claude Desktop Key"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            A descriptive name to identify this key
          </p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Optional description..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            rows={2}
          />
        </div>

        {/* Permission Group */}
        <div className="space-y-3">
          <Label>Permission Level *</Label>
          <div className="space-y-2">
            {MCP_PERMISSION_GROUPS.map((group) => {
              const isSelected = permissionGroup === group.id;
              return (
                <div
                  key={group.id}
                  onClick={() => !loading && setPermissionGroup(group.id)}
                  className={`
                    flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all
                    ${isSelected
                      ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2'
                      : 'border-border hover:border-muted-foreground/50 hover:bg-muted/50'
                    }
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    {permissionIcons[group.id]}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{group.displayName}</span>
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {group.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Expiration */}
        <div className="space-y-2">
          <Label>Expiration</Label>
          <Select value={expiresIn} onValueChange={setExpiresIn} disabled={loading}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="never">Never expires</SelectItem>
              <SelectItem value="30d">30 days</SelectItem>
              <SelectItem value="90d">90 days</SelectItem>
              <SelectItem value="1y">1 year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Creating...
            </>
          ) : (
            <>
              <Bot className="w-4 h-4 mr-2" />
              Create MCP Key
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );

  // Render success step
  const renderSuccess = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-green-500">
          <Check className="w-5 h-5" />
          MCP Key Created
        </DialogTitle>
        <DialogDescription>
          Your new MCP key has been created successfully
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
        <Alert className="border-amber-500 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-600 dark:text-amber-400">
            <strong>Important:</strong> Copy your MCP key now. You won't be able to see it again!
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label>Your MCP Key</Label>
          <div className="flex items-center gap-2">
            <div className={`flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all border transition-all duration-300 ${copied ? 'ring-2 ring-green-500 border-green-500' : ''}`}>
              {generatedKey}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
              className={`shrink-0 transition-all duration-300 ${copied ? 'border-green-500 text-green-500' : ''}`}
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Store this key securely. It will not be shown again.
          </p>
        </div>

        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <h4 className="font-medium">Quick Start</h4>
          <p className="text-sm text-muted-foreground">
            See the configuration examples in Settings to connect Claude Desktop, Cursor, or other AI assistants.
          </p>
        </div>

        {/* Confirmation checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="confirm-saved"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          <Label htmlFor="confirm-saved" className="text-sm cursor-pointer">
            I have securely saved this key
          </Label>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={handleClose} disabled={!confirmed}>
          Done
        </Button>
      </DialogFooter>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg">
        {step === 'form' ? renderForm() : renderSuccess()}
      </DialogContent>
    </Dialog>
  );
}

export default CreateMcpKeyModal;
